import { fetchAuthSession } from 'aws-amplify/auth';

// Apple IAP provider — wraps cordova-plugin-purchase (CdvPurchase v13+).
// Canonical two-stage pipeline per shared-docs/in-app-purchases.md (ported
// 2026-09-11 — the pre-canon handler read transaction.transactionId directly
// inside `approved` and POSTed from there, which happened to work because it
// never used the VerifiedReceipt shape, but skipped every guard below:
// no user-initiated window, no queue/drain, no cross-user protection at the
// client layer, no durable handling of re-delivered transactions).
//
// Critical rules enforced here (each prevents an App Store rejection or a
// silent revenue-invisible bug):
//   1. `approved` queues transactionId -> productId in `pendingVerify` (inside
//      the user-initiated window only) and calls transaction.verify() to
//      advance the state machine; `verified` drains the queue, filtered by
//      the VerifiedReceipt's OWN sourceReceipt.transactions, and finishes.
//   2. User-initiated guard with a 5-min window — receipts that fire on app
//      launch (re-delivery) for transactions NOT initiated by the current
//      user MUST be ignored, or a fresh account on a device with stale
//      receipts inherits Premium from the previous owner.
//   3. ALL of 6777006/6777003/6777010 (+ any /cancel/i message) from
//      store.order() are USER CANCELS — not errors. Handling only one code
//      missed a reviewer's sheet-dismiss and got BabyTrack rejected under
//      2.1(b). Restore additionally treats 6777011 (REFRESH_RECEIPTS) as
//      silent — the adapter runs the receipt refresh AFTER the restored
//      transactions are already delivered, so a failure there does not mean
//      the restore failed.
//   4. Verify backend writes via a silent, non-throwing POST (no toast) so a
//      stale/re-delivered receipt doesn't error on every app launch forever.
let _getAuthTokenFn = null;
let _isNative = null;

function isNativeIOS() {
  if (_isNative !== null) return _isNative;
  try {
    // eslint-disable-next-line global-require
    const { Capacitor } = require('@capacitor/core');
    _isNative = Capacitor.isNativePlatform();
  } catch {
    _isNative = false;
  }
  return _isNative;
}

// Apple product IDs
const PRODUCT_IDS = [
  'uk.co.wolftechnologies.cooksmith.premium.monthly',
  'uk.co.wolftechnologies.cooksmith.premium.yearly',
];

const PLAN_TO_PRODUCT = {
  monthly: 'uk.co.wolftechnologies.cooksmith.premium.monthly',
  annual: 'uk.co.wolftechnologies.cooksmith.premium.yearly',
};

// Reverse of PLAN_TO_PRODUCT — `approved` only ever sees a productId, never a
// plan key. Must stay in lockstep with APPLE_PRODUCT_MAP in the backend's
// utils/appleIAP.js.
const PRODUCT_TO_PLAN = {
  [PRODUCT_IDS[0]]: 'monthly',
  [PRODUCT_IDS[1]]: 'annual',
};

const USER_INITIATED_WINDOW_MS = 5 * 60 * 1000;

const CANCEL_CODES = new Set([
  6777006, // PAYMENT_CANCELLED — Apple's real SKErrorPaymentCancelled mapping
  6777003, // PURCHASE — generic code seen on legacy iPadOS sandbox cancels
  6777010, // UNKNOWN — Ask-to-Buy deferral / unknown failure on cancel
]);
// Restore additionally tolerates REFRESH_RECEIPTS (6777011) silently — see
// header comment.
const RESTORE_SILENT_CODES = new Set([...CANCEL_CODES, 6777011]);

let storeInstance = null;
let handlersRegistered = false;
let userInitiatedAt = 0;
let onPurchaseVerifiedCb = null;

// Transactions the user just approved (purchase or restore), keyed
// transactionId -> productId. The object CdvPurchase hands to `verified` is a
// VerifiedReceipt, which has NO `.transaction` and NO `.productId` — reading
// them (the pre-canon shape) means transactionId is always undefined and the
// backend is never called. `approved` is the only place the id is known: it
// queues here, `verified` drains.
const pendingVerify = new Map();

function getStore() {
  if (storeInstance) return storeInstance;
  if (!window.CdvPurchase) return null;
  storeInstance = window.CdvPurchase.store;
  return storeInstance;
}

async function getAuthTokenSafe() {
  if (_getAuthTokenFn) {
    try {
      return (await _getAuthTokenFn()) || '';
    } catch {
      /* fall through to Amplify session */
    }
  }
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() || '';
  } catch {
    return '';
  }
}

// Silent, non-throwing POST — a stale/re-delivered receipt on launch must
// never surface an error toast (shared-docs/error-surfacing.md). Collapses
// every failure to null; the caller treats null the same as a refusal.
async function silentVerify(transactionId, productId) {
  try {
    const token = await getAuthTokenSafe();
    const res = await fetch(
      `${process.env.REACT_APP_API_ENDPOINT}/subscription/apple-verify`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ transactionId, productId }),
      }
    );
    if (!res.ok) {
      console.warn('[IAP] apple-verify returned', res.status);
      return null;
    }
    return await res.json();
  } catch (err) {
    console.warn('[IAP] apple-verify request failed:', err.message);
    return null;
  }
}

/**
 * Initialize IAP: register products, set up approval/verified handlers,
 * initialize the store. Returns { success: true, productCount } or
 * { success: false, error }. Handlers are registered once; safe to call
 * again to retry initialization.
 */
export async function initializeIAP(onPurchaseVerified, getAuthToken) {
  // Store the auth token getter so the approved/verified handlers can use it.
  // Native Apple Sign In stores tokens in localStorage, not Amplify session,
  // so we need the AuthContext's getAuthToken which checks both.
  if (getAuthToken) _getAuthTokenFn = getAuthToken;
  if (onPurchaseVerified) onPurchaseVerifiedCb = onPurchaseVerified;
  if (!isNativeIOS()) return { success: false, error: 'not-ios' };

  const store = getStore();
  if (!store) {
    console.warn('[IAP] CdvPurchase not available — is cordova-plugin-purchase installed?');
    return { success: false, error: 'CdvPurchase not available. Please restart the app.' };
  }

  const { Platform, ProductType, LogLevel } = window.CdvPurchase;

  // Register products and handlers only once
  if (!handlersRegistered) {
    store.verbosity = LogLevel.DEBUG;

    store.register(
      PRODUCT_IDS.map((id) => ({
        id,
        type: ProductType.PAID_SUBSCRIPTION,
        platform: Platform.APPLE_APPSTORE,
      }))
    );

    // v13 has no store.when().error() — the correct form is store.error().
    store.error((err) => {
      console.error('[IAP] Store error:', err.code, err.message);
    });

    // Each handler gets its OWN store.when() call — chaining rejects the
    // whole init if any method in the chain doesn't exist.
    store.when().approved((transaction) => {
      if (!transaction.transactionId) {
        transaction.finish();
        return;
      }
      // Cross-user receipt contamination guard: if this `approved` event
      // fires outside a recent user-initiated purchase/restore window, the
      // receipt belongs to a previous owner of this Apple ID / device and
      // must NOT be claimed for the current user.
      if (Date.now() - userInitiatedAt >= USER_INITIATED_WINDOW_MS) {
        transaction.finish();
        return;
      }
      const productId = transaction.products?.[0]?.id;
      const plan = PRODUCT_TO_PLAN[productId];
      // Unknown product = the plugin's virtual `appstore.application`
      // transaction (always first in the Apple receipt, fires approved like
      // a sale) or an unregistered SKU — never queue it for the backend.
      if (!plan) {
        transaction.finish();
        return;
      }
      // CdvPurchase's Transaction exposes `expirationDate` (a Date), never
      // `expiryDate` — and the Apple adapter rarely populates it, so this
      // skip is best-effort only; the backend apple-verify enforces expiry.
      const expiresAt = transaction.expirationDate ? new Date(transaction.expirationDate) : null;
      if (expiresAt && expiresAt.getTime() <= Date.now()) {
        transaction.finish();
        return;
      }

      console.log('[IAP] approved, queuing for verify:', transaction.transactionId, productId);
      pendingVerify.set(transaction.transactionId, productId);
      transaction.verify(); // advance the state machine to `verified`
    });

    store.when().verified((receipt) => {
      // `receipt` is a VerifiedReceipt — it has NO .transaction / .productId.
      // The local transactions live on receipt.sourceReceipt.transactions;
      // drain only the ids `approved` queued (already window-gated) that
      // this exact receipt actually carries.
      const localIds = new Set(
        (receipt?.sourceReceipt?.transactions || []).map((t) => t.transactionId).filter(Boolean),
      );
      const writes = [];
      const callback = onPurchaseVerifiedCb; // snapshot — avoids a stale closure over the loop
      for (const [transactionId, productId] of pendingVerify) {
        if (localIds.size && !localIds.has(transactionId)) continue; // another receipt
        pendingVerify.delete(transactionId);
        writes.push(
          silentVerify(transactionId, productId).then((result) => {
            if (result && callback) callback(result);
            return result;
          })
        );
      }
      receipt.finish();
      if (writes.length) {
        // Refetch/callback only AFTER every write has settled — firing it
        // right after an un-awaited POST races the (would-be) row write.
        Promise.allSettled(writes);
      }
    });

    handlersRegistered = true;
  }

  // Initialize the store (safe to retry)
  try {
    await store.initialize([Platform.APPLE_APPSTORE]);
    console.log('[IAP] Store initialized');
    await store.update();
    console.log('[IAP] Products updated, count:', store.products.length);
    store.products.forEach((p) => {
      console.log('[IAP] Product:', p.id, 'canPurchase:', p.canPurchase, 'owned:', p.owned);
    });
    if (store.products.length === 0) {
      return { success: false, error: 'No products returned from the App Store. Check your subscription setup in App Store Connect.' };
    }
    return { success: true, productCount: store.products.length };
  } catch (err) {
    console.error('[IAP] Failed to initialize store:', err);
    return { success: false, error: err.message || 'Failed to connect to the App Store.' };
  }
}

/**
 * Get registered products with Apple-localized prices.
 */
export function getIAPProducts() {
  const store = getStore();
  if (!store) return [];

  return PRODUCT_IDS.map((id) => {
    const product = store.get(id);
    if (!product) return null;
    const offer = product.getOffer();
    const pricingPhase = offer?.pricingPhases?.[offer.pricingPhases.length - 1];
    return {
      productId: id,
      localizedPrice: pricingPhase?.price || '',
      price: String(pricingPhase?.priceMicros ? pricingPhase.priceMicros / 1000000 : ''),
    };
  }).filter(Boolean);
}

/**
 * Initiate a purchase for the given plan.
 * CdvPurchase v13: store.order() returns IError on failure, undefined on
 * success — it does NOT throw, so the return value must be checked.
 */
export async function purchasePlan(plan) {
  const store = getStore();
  if (!store) throw new Error('Store not available');

  const productId = PLAN_TO_PRODUCT[plan] || PLAN_TO_PRODUCT.monthly;
  const product = store.get(productId);
  if (!product) {
    if (store.products.length === 0) {
      throw new Error('Subscriptions are loading. Please wait a moment and try again.');
    }
    throw new Error(`Product not found: ${productId}`);
  }

  const offer = product.getOffer();
  if (!offer) throw new Error(`No offer for product: ${productId}`);

  console.log('[IAP] Ordering product:', productId);
  userInitiatedAt = Date.now(); // opens the cross-user contamination window
  const result = await store.order(offer);
  if (result && result.isError) {
    if (CANCEL_CODES.has(result.code) || /cancel/i.test(result.message || '')) {
      const cancelled = new Error('canceled');
      cancelled.cancelled = true;
      throw cancelled;
    }
    const err = new Error(result.message || 'Purchase failed');
    err.code = result.code;
    throw err;
  }
  return result;
}

/**
 * Restore previous purchases (required by Apple).
 * Same IError contract as store.order() — resolves rather than throws, so
 * the wrapper must check `result.isError` itself. Cancel codes AND
 * REFRESH_RECEIPTS (6777011) must stay silent — a red toast on a purchase
 * control (including Restore) is the Guideline 2.1(b) pattern.
 */
export async function restoreIAPPurchases() {
  const store = getStore();
  if (!store) throw new Error('Store not available');
  userInitiatedAt = Date.now(); // opens the cross-user contamination window
  const result = await store.restorePurchases();
  if (result && result.isError
      && !RESTORE_SILENT_CODES.has(result.code)
      && !/cancel/i.test(result.message || '')) {
    const err = new Error(result.message || 'Restore failed. Please try again.');
    err.code = result.code;
    throw err;
  }
  return result;
}

export function openSubscriptionManagement() {
  window.open('itms-apps://apps.apple.com/account/subscriptions', '_system');
}

export { isNativeIOS };
